import {
  useFirebaseAuthentication,
  getOrCreateIdentity,
  Identity_SubscribeToChanges,
  parseJwt,
} from '@rabbit/data/portal';
import {
  DTConsumer_Private,
  DTRepairer_Private,
  DTRetailer_Private,
  DTTenant_Public,
  Permissions,
  PersonaIdTypeSplitter,
  PersonaTypeSingleLetter,
} from '@rabbit/data/types';
import React, {
  createContext,
  useEffect,
  useState,
  useCallback,
  useContext,
} from 'react';
import { SagePersonaChecker } from './SagePersonaChecker';
import {
  Identity_EnsureIdentityExistsForAuthLikeObject,
  Sage_Identity_CreatePersonasForIdentity,
} from '@rabbit/bizproc/client';
import { SageAuthFormValuesShape } from '../components/templates/AuthForm/AuthForm';
import { init } from '@amplitude/analytics-browser';
import { isAuthPartner } from '../utils/helpers';
import { useSageAPI } from '@rabbit/bizproc/react';
import { ExtendedDecodedIdToken } from '@rabbit/bizproc/server-logic';
import { AppContext } from '@rabbit/app-context';
import { useTranslation } from 'react-i18next';
import {
  nestApiUpdateTenantLastLogined,
  Permissions2string,
  nestApiGetSecret,
} from '@rabbit/bizproc/core';
import { navigate, useQueryParams } from 'raviger';
import { SAGE_ROUTE_NAME } from '@rabbit/config/enums';
import { firebaseAuth } from '@rabbit/firebase/adapter-react';
import { User } from 'firebase/auth';
// TODO: ADD A PROPER TYPE FOR USER!
// TODO: Make this better equipped to handle multiple personas, instead of 2 state per persona - DC

// Cache for the AMPLITUDE_API_KEY to avoid multiple API calls
let amplitudeApiKeyCache: string | null = null;

export interface UserInterface {
  user: any;
  handleSignout: () => void;
  repairerPersonaData: DTRepairer_Private | null;
  repairerPersonaId: string | null;
  retailerPersonaData: DTRetailer_Private | null;
  retailerPersonaId: string | null;
  accessToken: ExtendedDecodedIdToken | null;
  createSageRepairerUser: (
    formValues: SageAuthFormValuesShape
  ) => Promise<void>;
  sageSignIn: (values: SageAuthFormValuesShape) => Promise<void>;
  activeTenantInfo: DTTenant_Public | null;
  personasLoaded: boolean; // true when SagePersonaChecker reports completion
}

type UserProviderWrapperProps = {
  children: React.ReactNode;
};

const UserContext = createContext<UserInterface | null>(null);

const UserProviderWrapper = ({ children }: UserProviderWrapperProps) => {
  const { refreshClaimsForIdentity } = useSageAPI();
  const { t } = useTranslation();
  const { config } = useContext(AppContext);

  const {
    user: authUser,
    signOut,
    createWithEmail,
    signInWithEmail,
    setAuthPersistence,
  } = useFirebaseAuthentication();

  const [user, setUser] = useState<any>();
  const params = useQueryParams();
  const [repairerPersonaData, setRepairerPersonaData] =
    useState<DTRepairer_Private | null>(null);
  const [installerPersonaData, setInstallerPersonaData] =
    useState<DTRepairer_Private | null>(null);
  const [repairerPersonaId, setRepairerPersonaId] = useState<string | null>(
    null
  );
  const [installerPersonaId, setInstallerPersonaId] = useState<string | null>(
    null
  );
  const [retailerPersonaData, setRetailerPersonaData] =
    useState<DTRetailer_Private | null>(null);
  const [retailerPersonaId, setRetailerPersonaId] = useState<string | null>(
    null
  );
  const [consumerPersonaId, setConsumerPersonaId] = useState<string | null>(
    null
  );
  const [consumerPersonaData, setConsumerPersonaData] =
    useState<DTConsumer_Private | null>(null);
  const [activeTenantInfo, setActiveTenantInfo] =
    useState<DTTenant_Public | null>(null);
  // Tracks when SagePersonaChecker has finished loading personas
  const [personasLoaded, setPersonasLoaded] = useState<boolean>(false);

  useEffect(() => {
    const initUser = (user: (User & { accessToken: string }) | null) => {
      if (!user) return;
      const parsedJWT = parseJwt(user?.accessToken as string);
      console.debug(parsedJWT, 'parsedJWT');
      // TODO: Remove at the soonest
      // Updating config should never happen on FE

      const permissionsFromRootWarrantor =
        parsedJWT[
          `${
            PersonaTypeSingleLetter.Warrantor +
            PersonaIdTypeSplitter +
            t('tenantLink')
          }`
        ];

      const permissionsFromRootRepairer =
        parsedJWT[
          PersonaTypeSingleLetter.Repairer +
            PersonaIdTypeSplitter +
            t('tenantLink')
        ];

      //aka is the user a tenant
      if (
        permissionsFromRootWarrantor?.includes(
          Permissions2string([Permissions.Owner])
        )
      ) {
        if (config.NAVIGATION.RESTRICTED) {
          config.NAVIGATION.RESTRICTED_PATHS =
            config.NAVIGATION.RESTRICTED_USER_PATHS.Warrantor;
        }
      }

      // aka is the user a partner
      if (
        permissionsFromRootRepairer &&
        !permissionsFromRootRepairer?.includes(
          Permissions2string([Permissions.Owner])
        )
      ) {
        if (config.NAVIGATION.RESTRICTED) {
          config.NAVIGATION.RESTRICTED_PATHS =
            config.NAVIGATION.RESTRICTED_USER_PATHS.ExternalRepairer;
        }
        if (config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH) {
          config.NAVIGATION.SAGE.DEFAULT_PATH =
            config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH;
        }
      }
    };
    const unsubscribe = firebaseAuth.onAuthStateChanged(async (user) => {
      // Check if current path is a public route that doesn't require authentication
      const isPublicRoute = (pathname: string): boolean => {
        const publicRoutes = [
          SAGE_ROUTE_NAME.LOGIN,
          SAGE_ROUTE_NAME.SIGNUP,
          SAGE_ROUTE_NAME.FORGOT_PASSWORD,
          SAGE_ROUTE_NAME.TERMS_AND_CONDITIONS,
          SAGE_ROUTE_NAME.PRIVACY_POLICY,
          SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT,
          SAGE_ROUTE_NAME.PLATFORM_SERVICES_AGREEMENT_OLD,
          SAGE_ROUTE_NAME.CONFIRM_EMAIL,
          SAGE_ROUTE_NAME.RESET_PASSWORD,
          SAGE_ROUTE_NAME.REGISTER_PRODUCT_INSTALLER,
        ];

        return publicRoutes.some((route) => pathname.startsWith(route));
      };

      if (!user && !isPublicRoute(location.pathname)) {
        navigate('/login');
      }
      setUser(user);
      // Reset personas loaded flag; will be set to true by SagePersonaChecker when done
      setPersonasLoaded(false);
      initUser(user as any);
      if (
        config.PARTNERS.PARTNER_TABLE.SHOW_LAST_LOGIN_TIME &&
        user &&
        !isPublicRoute(location.pathname)
      ) {
        // Update the last login time in the tenant document via an API call
        // This is done in a fire-and-forget manner to avoid blocking the user interface
        nestApiUpdateTenantLastLogined();
      }
    });

    return unsubscribe;
  }, []);

  // Create user with repairer persona
  const createSageRepairerUser = async (values: SageAuthFormValuesShape) => {
    try {
      const res = await createWithEmail(values.email, values.password);
      if (res?.user.uid) await CreatePersonasForNewSageUser(res?.user, values);
      values.rememberMe
        ? await setAuthPersistence('local')
        : await setAuthPersistence('session');

      await getOrCreateIdentity();
      setUser(res.user);
    } catch (e) {
      console.error(e);
    }
  };

  const sageSignIn = async (values: SageAuthFormValuesShape) => {
    try {
      values.rememberMe
        ? await setAuthPersistence('local')
        : await setAuthPersistence('session');
      const res = await signInWithEmail(values.email, values.password);

      if (res?.user?.uid) {
        void refreshClaimsForIdentity({ uid: res.user.uid });
        if (config.PARTNERS.PARTNER_TABLE.SHOW_LAST_LOGIN_TIME) {
          // Update the last login time in the tenant document via an API call
          // This is done in a fire-and-forget manner to avoid blocking the user interface
          nestApiUpdateTenantLastLogined();
        }
      }
      await getOrCreateIdentity();
      setUser(res.user);
    } catch (e) {
      console.error(e);
      throw e;
    }
  };

  // Function to fetch AMPLITUDE_API_KEY from secrets API using the wrapper
  const getAmplitudeApiKey = useCallback(async (): Promise<string> => {
    // Return cached key if available
    if (amplitudeApiKeyCache) {
      console.log('Using cached AMPLITUDE_API_KEY');
      return amplitudeApiKeyCache;
    }

    try {
      const result = await nestApiGetSecret({
        secretName: 'AMPLITUDE_API_KEY',
      });

      // Access properties from the wrapped response structure
      if (result.data.success && result.data.value) {
        // Cache the key for future use
        amplitudeApiKeyCache = result.data.value;
        console.log('Fetched and cached AMPLITUDE_API_KEY');
        return result.data.value;
      } else {
        throw new Error(
          result.data.error || 'Failed to retrieve AMPLITUDE_API_KEY'
        );
      }
    } catch (error) {
      console.error('Error fetching AMPLITUDE_API_KEY:', error);
      // Fallback to environment variable if API fails
      const fallbackKey = import.meta.env.VITE_AMPLITUDE_API_KEY;
      if (fallbackKey) {
        console.warn(
          'Using fallback AMPLITUDE_API_KEY from environment variable'
        );
        return fallbackKey;
      }
      throw new Error(
        'AMPLITUDE_API_KEY not available from API or environment'
      );
    }
  }, []);

  useEffect(() => {
    const initAmplitude = async (uuid: string) => {
      try {
        console.log('Initializing Amplitude');
        const apiKey = await getAmplitudeApiKey();
        init(apiKey, uuid, {
          instanceName: import.meta.env.VITE_FIREBASE_MODE ?? '-',
          defaultTracking: {
            pageViews: {
              trackHistoryChanges: 'all',
            },
          },
        });
      } catch (error) {
        console.error('Failed to initialize Amplitude:', error);
      }
    };

    if (authUser.data?.uid) void initAmplitude(authUser.data?.uid);
    if (authUser.data?.uid) {
      console.log('Found user, initializing Amplitude');
      void initAmplitude(authUser.data?.uid);
    } else console.log('No user found, not initializing Amplitude');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser.isLoading, authUser.data?.uid, getAmplitudeApiKey]);

  const handleSignout = useCallback(() => {
    signOut();
    setUser(null);
    setRepairerPersonaData(null);
    setRepairerPersonaId(null);
    setInstallerPersonaId(null);
    setRetailerPersonaData(null);
    setRetailerPersonaId(null);
    setConsumerPersonaData(null);
    setConsumerPersonaId(null);
    setActiveTenantInfo(null);
    setPersonasLoaded(false);
    navigate('/login');
  }, [signOut]);

  useEffect(() => {
    if (params[0] && params[0].sku) {
      window.localStorage.setItem('LRP_Vendablempn', params[0].sku);
    } else {
      window.localStorage.removeItem('LRP_Vendablempn');
    }
    if (params[0] && params[0].sn) {
      window.localStorage.setItem('LRP_VendableSn', params[0].sn);
    } else {
      window.localStorage.removeItem('LRP_VendableSn');
    }
  }, [JSON.stringify(params)]);

  const accessToken = user && parseJwt(user.accessToken);
  const contextValues = {
    user,
    accessToken,
    handleSignout,
    repairerPersonaData,
    repairerPersonaId,
    retailerPersonaData,
    installerPersonaId,
    installerPersonaData,
    consumerPersonaId,
    consumerPersonaData,
    retailerPersonaId,
    createSageRepairerUser,
    sageSignIn,
    activeTenantInfo,
    personasLoaded,
  };

  if (isAuthPartner(contextValues, t)) {
    if (
      location?.pathname === SAGE_ROUTE_NAME.DEFAULT ||
      config.NAVIGATION.SAGE.PARTNER_RESTRICTED_PATH.includes(
        location?.pathname
      )
    ) {
      navigate(config.NAVIGATION.SAGE.DEFAULT_PARTNER_PATH);
    }
  }

  return (
    <UserContext.Provider value={contextValues}>
      {user && (
        <SagePersonaChecker
          setRepairerPersonaData={setRepairerPersonaData}
          setRepairerPersonaId={setRepairerPersonaId}
          setInstallerPersonaId={setInstallerPersonaId}
          setInstallerPersonaData={setInstallerPersonaData}
          setRetailerPersonaData={setRetailerPersonaData}
          setRetailerPersonaId={setRetailerPersonaId}
          setConsumerPersonaData={setConsumerPersonaData}
          setConsumerPersonaId={setConsumerPersonaId}
          setActiveTenantInfo={setActiveTenantInfo}
          onLoadingComplete={() => setPersonasLoaded(true)}
        />
      )}
      {children}
    </UserContext.Provider>
  );
};

export { UserContext, UserProviderWrapper };

/* -------------------------------------------------------------------------- */
/*                                   Helpers                                  */
/* -------------------------------------------------------------------------- */
async function CreatePersonasForNewSageUser(
  fbUser: any, //todo
  formData: SageAuthFormValuesShape
) {
  try {
    const identities = await Identity_EnsureIdentityExistsForAuthLikeObject(
      fbUser
    );
    if (!identities?.identity_private)
      throw new Error('Failed to get or create identity');

    Identity_SubscribeToChanges(identities.identity_private);

    const personas = await Sage_Identity_CreatePersonasForIdentity(
      identities?.identity.docid,
      formData
    );

    if (!personas?.repairer_private)
      throw new Error('Failed to create repairer persona');
    console.log('New repairer persona created successfully');

    return personas;
  } catch (error) {
    console.error(error);
    return null;
  }
}
